import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listarMovimentacoes } from '../../api/movimentacoes.js';
import type { FiltroDeMovimentacoes, Movimentacao } from '../../api/movimentacoes.js';
import { intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { filialDoFiltro } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../../componentes/Filtros.js';
import { IconeMovimentacoes } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { AutorDaMovimentacao } from './AutorDaMovimentacao.js';
import { CaminhoDeDepositos } from './CaminhoDeDepositos.js';
import { ChipDeFamilia } from './ChipDeFamilia.js';
import {
  FAMILIA_TODAS,
  familiaDaMovimentacao,
  filtroDaFamilia,
  OPCOES_DE_FAMILIA,
  rotuloDoStatus,
  tomDoStatus,
} from './familia.js';
import { contagemDeMovimentacoes, inicioDoPeriodo, PERIODO_PADRAO, PERIODOS } from './formato.js';
import { LegendaDasFamilias } from './LegendaDasFamilias.js';
import { escolhido, TODOS, useOpcoesDeDispositivo, useOpcoesDeOperador } from './opcoesDeFiltro.js';
import { caminhoDaTrilha } from './recorte.js';
import { useValorAdiado } from './useValorAdiado.js';

const COLUNAS: readonly Coluna<Movimentacao>[] = [
  {
    chave: 'registradoEm',
    rotulo: 'Horário',
    largura: '140px',
    celula: (movimentacao) => (
      <span className="movimentacoes__horario">{formatarDataHora(movimentacao.registradoEm)}</span>
    ),
  },
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '15%',
    celula: (movimentacao) => (
      <>
        <Link className="tabela__abrir" to={caminhoDaTrilha(movimentacao.produto)}>
          {movimentacao.produto}
        </Link>
        {movimentacao.lote ? (
          <span className="movimentacoes__lote">Lote {movimentacao.lote}</span>
        ) : null}
      </>
    ),
  },
  {
    chave: 'caminho',
    rotulo: 'Origem → destino',
    celula: (movimentacao) => (
      <CaminhoDeDepositos
        depositoOrigem={movimentacao.depositoOrigem}
        filialOrigem={movimentacao.filialOrigem}
        depositoDestino={movimentacao.depositoDestino}
        filialDestino={movimentacao.filialDestino}
      />
    ),
  },
  {
    chave: 'quantidade',
    rotulo: 'Qtd.',
    largura: '110px',
    alinhamento: 'direita',
    celula: (movimentacao) => (
      <span className="movimentacoes__quantidade">{movimentacao.quantidade}</span>
    ),
  },
  {
    chave: 'familia',
    rotulo: 'Movimentação',
    largura: '180px',
    celula: (movimentacao) => <ChipDeFamilia movimentacao={movimentacao} />,
  },
  {
    chave: 'status',
    rotulo: 'Status',
    largura: '150px',
    celula: (movimentacao) => (
      <ChipStatus
        tom={tomDoStatus(movimentacao.status)}
        rotulo={rotuloDoStatus(movimentacao.status)}
        tamanho="tabela"
      />
    ),
  },
  {
    chave: 'autor',
    rotulo: 'Autor',
    largura: '17%',
    celula: (movimentacao) => <AutorDaMovimentacao autor={movimentacao} />,
  },
];

/** A linha que exige decisão humana não pode se parecer com a rotina (UC08 4a). */
function classeDaLinha(movimentacao: Movimentacao): string | undefined {
  return familiaDaMovimentacao(movimentacao) === 'origemIndeterminada'
    ? 'movimentacoes__linha--indeterminada'
    : undefined;
}

/**
 * Movimentações registradas em ETAPA ÚNICA (RF-076, RF-079 parcial, UC08).
 *
 * É o registro do app anterior: origem e destino num lançamento só. Continua
 * aqui porque aparelho com o app antigo ainda manda por esse caminho, e cada
 * entrega da movimentação em duas etapas também aparece nesta trilha — é uma
 * linha de movimentação como as outras.
 *
 * Tela de **leitura** para os três perfis: movimentação registrada não se
 * edita nem se cancela por aqui — trilha que se corrige deixa de ser trilha.
 */
export function AbaEtapaUnica() {
  const [periodo, definirPeriodo] = useState(PERIODO_PADRAO);
  const [produto, definirProduto] = useState('');
  const [deposito, definirDeposito] = useState('');
  const [filial, definirFilial] = useState('');
  const [operadorId, definirOperadorId] = useState(TODOS);
  const [dispositivoId, definirDispositivoId] = useState(TODOS);
  const [familia, definirFamilia] = useState<string>(FAMILIA_TODAS);
  const [pagina, definirPagina] = useState(1);

  const produtoAdiado = useValorAdiado(produto);
  const depositoAdiado = useValorAdiado(deposito);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeMovimentacoes>(
    () => ({
      pagina,
      ...seInformado('de', inicioDoPeriodo(periodo)),
      ...seInformado('produto', produtoAdiado),
      ...seInformado('deposito', depositoAdiado),
      ...seInformado('codFil', codFil),
      ...seInformado('operadorId', escolhido(operadorId)),
      ...seInformado('dispositivoId', escolhido(dispositivoId)),
      ...filtroDaFamilia(familia),
    }),
    [pagina, periodo, produtoAdiado, depositoAdiado, codFil, operadorId, dispositivoId, familia],
  );

  const buscar = useCallback(() => listarMovimentacoes(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  const opcoesDeOperador = useOpcoesDeOperador();
  const opcoesDeDispositivo = useOpcoesDeDispositivo();

  /** Trocar o recorte volta para a primeira página: a 7 do filtro antigo não existe no novo. */
  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="movimentacoes__aba">
      <div className="movimentacoes__introducao">
        <h2 className="movimentacoes__subtitulo">Movimentações registradas em etapa única</h2>
        <LegendaDasFamilias />
      </div>

      <FaixaDeFiltros rotulo="Filtros das movimentações em etapa única">
        <CampoBusca
          rotulo="Buscar por produto"
          valor={produto}
          aoMudar={(valor) => {
            recortar(() => {
              definirProduto(valor);
            });
          }}
          largura={220}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        <CampoBusca
          rotulo="Buscar por depósito"
          valor={deposito}
          aoMudar={(valor) => {
            recortar(() => {
              definirDeposito(valor);
            });
          }}
          largura={190}
        />
        <FiltroSelecao
          rotulo="Período"
          valor={periodo}
          opcoes={PERIODOS}
          aoMudar={(valor) => {
            recortar(() => {
              definirPeriodo(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Tipo de movimentação"
          valor={familia}
          opcoes={OPCOES_DE_FAMILIA}
          aoMudar={(valor) => {
            recortar(() => {
              definirFamilia(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Operador"
          valor={operadorId}
          opcoes={opcoesDeOperador}
          aoMudar={(valor) => {
            recortar(() => {
              definirOperadorId(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Dispositivo"
          valor={dispositivoId}
          opcoes={opcoesDeDispositivo}
          aoMudar={(valor) => {
            recortar(() => {
              definirDispositivoId(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>{contagemDeMovimentacoes(consulta.dados.total)}</ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando movimentações…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeMovimentacoes tamanho={28} />}
            titulo="Nenhuma movimentação neste recorte."
            detalhe="Amplie o período ou limpe os filtros para ver mais registros."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(movimentacao) => movimentacao.id}
            descricao="Movimentações registradas"
            classeDaLinha={classeDaLinha}
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'movimentação', 'movimentações')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
